import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';
import {
  isPushSupported,
  enablePush,
  disablePush,
  getCurrentSubscription,
  sendTestPush,
} from '../lib/push';
import { usePWAInstall } from '../hooks/usePWAInstall';

/* App (phone/browser) notifications + PWA install - available to ALL users. */
const AppNotificationsCard = ({ user }) => {
  const [enabled, setEnabled] = useState(user?.pushEnabled || false);
  const [time, setTime] = useState(user?.pushReminderTime || '08:00');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [supported] = useState(() => isPushSupported());
  const { canInstall, isInstalled, isIOS, promptInstall } = usePWAInstall();

  useEffect(() => {
    if (!supported) return;
    // Sync the toggle with the actual device subscription state
    (async () => {
      try {
        const sub = await getCurrentSubscription();
        if (!sub) setEnabled(false);
      } catch (e) {
        /* ignore */
      }
    })();
  }, [supported]);

  const showStatus = (msg) => {
    setStatus(msg);
    setTimeout(() => setStatus(''), 4000);
  };

  const handleToggle = async () => {
    if (busy) return;
    setBusy(true);
    setStatus('');
    try {
      if (!enabled) {
        await enablePush();
        // persist preferred time on first enable
        await axios.put(`${API}/push/settings`, { pushEnabled: true, pushReminderTime: time });
        setEnabled(true);
        showStatus('✅ Notifications enabled on this device.');
      } else {
        await disablePush();
        setEnabled(false);
        showStatus('🔕 Notifications silenced on this device.');
      }
    } catch (err) {
      showStatus(`⚠️ ${err.message || 'Could not update notifications.'}`);
    } finally {
      setBusy(false);
    }
  };

  const handleTimeChange = async (e) => {
    const newTime = e.target.value;
    setTime(newTime);
    if (enabled) {
      try {
        await axios.put(`${API}/push/settings`, { pushReminderTime: newTime });
      } catch (err) {
        /* ignore */
      }
    }
  };

  const handleTest = async () => {
    setBusy(true);
    try {
      await sendTestPush();
      showStatus('🔔 Test notification sent! Check your device.');
    } catch (err) {
      showStatus(`⚠️ ${err.response?.data?.detail || 'Could not send test.'}`);
    } finally {
      setBusy(false);
    }
  };

  const handleInstall = async () => {
    await promptInstall();
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6 mb-4" data-testid="app-notifications-card">
      <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
        <span className="text-xl">📲</span> Phone Notifications
      </h3>
      <p className="text-gray-500 text-sm mb-4">
        Get a gentle reminder on your phone (no email needed). Free for everyone.
      </p>

      {!supported ? (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-600">
          Your current browser doesn&apos;t support push notifications. On iPhone, install the app to your
          Home Screen first (button below), then enable notifications.
        </div>
      ) : (
        <>
          {/* Enable / Silence toggle */}
          <div className={`p-4 rounded-xl border-2 mb-3 ${enabled ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xl">🔔</span>
                <label className="font-medium text-gray-900">Daily push reminder</label>
                {enabled && (
                  <span className="bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full">On</span>
                )}
              </div>
              <button
                data-testid="push-toggle-button"
                onClick={handleToggle}
                disabled={busy}
                className={`relative w-12 h-6 rounded-full transition-colors disabled:opacity-50 ${
                  enabled ? 'bg-emerald-500' : 'bg-gray-300'
                }`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                  enabled ? 'translate-x-6' : ''
                }`} />
              </button>
            </div>

            {enabled && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reminder time
                </label>
                <input
                  data-testid="push-time-input"
                  type="time"
                  value={time}
                  onChange={handleTimeChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
                />
                <button
                  data-testid="push-test-button"
                  onClick={handleTest}
                  disabled={busy}
                  className="mt-3 text-sm text-emerald-600 hover:text-emerald-700 font-medium disabled:opacity-50"
                >
                  Send a test notification →
                </button>
              </div>
            )}
          </div>

          {status && (
            <p data-testid="push-status" className="text-sm text-gray-600 mb-3">{status}</p>
          )}
        </>
      )}

      {/* Install App */}
      <div className="mt-2 pt-4 border-t border-gray-100">
        {isInstalled ? (
          <p className="text-sm text-emerald-600 font-medium flex items-center gap-2">
            <span>✅</span> App installed on this device
          </p>
        ) : canInstall ? (
          <button
            data-testid="pwa-install-button"
            onClick={handleInstall}
            className="w-full py-3 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-xl transition flex items-center justify-center gap-2"
          >
            <span>⬇️</span> Install App on this device
          </button>
        ) : isIOS ? (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800" data-testid="ios-install-hint">
            <strong>📱 Install on iPhone:</strong> Tap the Share icon{' '}
            <span className="font-mono">⎙</span> in Safari, then choose{' '}
            <strong>&quot;Add to Home Screen&quot;</strong>.
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-700" data-testid="manual-install-hint">
            <strong>📲 Install this app:</strong> Open your browser menu{' '}
            <span className="font-mono">⋮</span> (top-right) and tap{' '}
            <strong>&quot;Install app&quot;</strong> or <strong>&quot;Add to Home screen&quot;</strong>.
            <span className="block mt-1 text-gray-500">Already installed? Open it from your Home screen icon.</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppNotificationsCard;
